import { useEffect, useState } from "react";
import {
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_BEHAVIORS_TYPE,
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_DEMOGRAPHICS_TYPE,
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_INTERESTS_TYPE,
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS,
  META,
  metaCurrenciesWithDecimals,
  MIN_WIDTH_SCREEN_TO_SHOW_CHAT,
} from "../../../../utils/constants";
import { useTranslationApp } from "../../../../lib/i18next";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { Controller, useFormContext } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import {
  People,
  Person,
  Favorite,
  TrendingUp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocationOnOutlined,
  Paid,
} from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Slider, useMediaQuery, useTheme } from "@mui/material";
import { addDays, addMinutes, isPast } from "date-fns";
import { marksAge } from "../../../../utils/options";
import TextField from "../../../Form/TextField";
import { textNumberCompactFormat } from "../../../../utils/numbers";
import AdsGroupFormCustomAudiencesList from "../../../../pages/AdsCampaign/components/AdsGroupFormCustomAudiencesList";
import {
  BOX_SHADOW_THEME,
  GREY_SLIDER_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import CheckBoxes from "../../../Form/CheckBoxes";
import SearchSegmentationsField from "../../../Form/SearchSegmentationsField";
import { waitDelay } from "../../../../utils/date";
import { getCurrencyAdAccountPlatform } from "../../../../actions/getters";
import { useDispatchApp } from "../../../../lib/redux";
import NumberField from "../../../Form/NumberField";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import {
  ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE,
  AGE_RANGE_PLACEHOLDER,
  BEHAVIORS_PLACEHOLDER,
  BUDGET_CAMPAIGN_DATES_PLACEHOLDER,
  BUDGET_CONFIGURATION_PLACEHOLDER,
  COPY_VALUE_IN_OTHER_ADGROUPS,
  DEMOGRAPHICS_BASIC_PLACEHOLDER,
  DEMOGRAPHICS_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  FEMALES,
  FIELD_REQUIRED,
  FREQUENCY_CONTROL_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  LIFETIME_BUDGET_PLACEHOLDER,
  LOCATIONS_PLACEHOLDER,
  MALES,
  READY,
  RETARGETING_EMPTY_SEGMENTATION_CLARIFICATION,
  START_DATE_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import DateTimeField from "../../../Form/DateTimeField";
import ReservationEstimatesPreview from "./ReservationEstimatesPreview";
import BTReservedFrequencyControlMeta from "./BTReservedFrequencyControlMeta";
import CardContainer from "../../../Card/CardContainer";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";

const platform = META;
const grid = {
  xs: 12,
  lg: 6,
};
const gridDates = {
  xs: 12,
  xl: 6,
};
const AdGroupBTReservedMetaForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    control,
    formState,
    watch,
    sectionSelected,
    isGeneralDisabledFields,
    trigger,
    setValue,
    getValues,
    isChatVisible,
  } = useFormContext();
  useFocusErrorForm(formState);

  const errors = formState.errors;

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isLargeScreen = useMediaQuery(
    () => `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const showSlideChat = isChatVisible && isLargeScreen;

  const adsGroups = watch("adsGroups");
  const adGroupID = sectionSelected.adGroupID;
  const index = adsGroups.findIndex((item) => item.id === adGroupID);
  const BASIC_PATH = `adsGroups.[${index}]`;

  const status = watch(`${BASIC_PATH}.status`) || ADS_CAMPAIGN_STATUS_DRAFT;
  const platformData = watch(`platformsData.${platform}`) || {};
  const isIncompatibleCampaign = platformData?.isIncompatible;
  const isAdGroupCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;
  const useCampaignBudget = platformData?.useCampaignBudget || false;

  const objective = platformData?.objective;
  const genders = watch(`${BASIC_PATH}.genders`) || {};
  const language = watch("language") || "";
  const startDate = watch(`${BASIC_PATH}.startDate`) || null;
  const customAudiences = watch(`${BASIC_PATH}.customAudiences`) || [];
  const isIncompatibleAdGroup = watch(`${BASIC_PATH}.isIncompatible`) || false;

  const isDisabledFields =
    isGeneralDisabledFields || isIncompatibleCampaign || isIncompatibleAdGroup;

  const hasCustomAudiences = customAudiences?.length > 0;

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform,
      integrationID: platformData?.integrationID,
      adAccountID: platformData?.adAccountID,
    })
  );

  // Date logic for startDate field
  const now = new Date();
  const minStartDate = addMinutes(now, 15); // Minimum 15 minutes from now
  const startDateValue = startDate ? new Date(startDate) : null;

  // Determine if startDate should be disabled
  const isStartDateDisabled = (() => {
    if (isDisabledFields) return true;

    // If it's a draft (not created), always allow modification
    if (!isAdGroupCreated) return false;

    // If it's created and has a startDate
    if (isAdGroupCreated && startDateValue) {
      // Allow modification only if the date is in the future
      return isPast(startDateValue);
    }

    return false;
  })();

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    const adsGroups = getValues(`adsGroups`);

    adsGroups.forEach((adGroup, currentIndexAdGroup) => {
      if (adGroup.platform !== platform) return;

      setValue(`adsGroups.[${currentIndexAdGroup}].${type}`, value);
      if (type === "locations") {
        setValue(`adsGroups.[${currentIndexAdGroup}].estimationID`, null);
      }
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };
  //To reset values component when change selectedLevel
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  const [interestsOpen, setInterestsOpen] = useState(true);
  const [behaviorOpen, setBehaviorOpen] = useState(true);
  const [demographyOpen, setDemographyOpen] = useState(true);

  if (isLoading) return null;

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "background.default",
        minHeight: "100vh",
        flexDirection: isTabletOrMobile || showSlideChat ? "column" : "row",
        color: "text.primary",
        display: "flex",
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth:
            isTabletOrMobile || showSlideChat ? "100%" : "calc(100% - 420px)",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          order: isTabletOrMobile || showSlideChat ? 2 : 1,
        }}
      >
        {/* Budget configuration */}
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {useCampaignBudget ? (
                <CalendarMonthIcon
                  sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                />
              ) : (
                <Paid sx={{ color: "#FFFFFF", width: 24, height: 24 }} />
              )}

              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {useCampaignBudget
                  ? t(BUDGET_CAMPAIGN_DATES_PLACEHOLDER)
                  : t(BUDGET_CONFIGURATION_PLACEHOLDER)}
              </Typography>
            </Box>
          }
        >
          <Grid container spacing={3}>
            {/* Budget configuration */}
            <Grid item {...grid}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography variant="body1">
                  {t(LIFETIME_BUDGET_PLACEHOLDER, {
                    currency: currency.toUpperCase(),
                  })}
                </Typography>
                <TextField
                  name={`${BASIC_PATH}.budget`}
                  disabled={isDisabledFields}
                  fullWidth
                  variant="filled"
                  InputProps={{
                    inputComponent: metaCurrenciesWithDecimals.includes(
                      currency
                    )
                      ? NumberDecimalField
                      : NumberField,
                  }}
                  control={control}
                  errors={errors}
                  rules={{}}
                />
              </Box>
            </Grid>

            {/* Start Date */}
            <Grid item {...gridDates}>
              <Typography sx={{ mb: 1 }} variant="body1">
                {t(START_DATE_PLACEHOLDER)}
              </Typography>
              <DateTimeField
                disabled={isStartDateDisabled}
                minDate={minStartDate}
                name={`${BASIC_PATH}.startDate`}
                id={`${BASIC_PATH}.startDate`}
                actionsChange={(value) => {
                  if (
                    value &&
                    new Date(value).getTime() < new Date(minStartDate).getTime()
                  ) {
                    setValue(`${BASIC_PATH}.startDate`, minStartDate, {
                      shouldValidate: true,
                    });
                  }
                }}
                control={control}
                errors={errors}
                showStartNowButton={true}
              />
            </Grid>

            {/* End Date */}
            <Grid item {...gridDates}>
              <Typography sx={{ mb: 1 }} variant="body1">
                {t(END_DATE_PLACEHOLDER)}
              </Typography>
              <DateTimeField
                disabled={isDisabledFields}
                name={`${BASIC_PATH}.endDate`}
                id={`${BASIC_PATH}.endDate`}
                minDate={addDays(new Date(), 1)}
                actionsChange={() => {
                  trigger(`${BASIC_PATH}.budget`);
                }}
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
        </CardContainer>

        {/* Frequency Control */}
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(FREQUENCY_CONTROL_PLACEHOLDER)}
              </Typography>
            </Box>
          }
        >
          <BTReservedFrequencyControlMeta
            name={`${BASIC_PATH}.frequency`}
            control={control}
            disabled={isDisabledFields}
          />
        </CardContainer>

        {/* Audience Segmentation */}
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <People
                sx={{
                  color: "#FFFFFF",
                  width: 24,
                  height: 24,
                  fontWeight: "bold",
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {t(ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE)}
              </Typography>
            </Box>
          }
        >
          <Box sx={{ spaceY: 4 }}>
            <AdsGroupFormCustomAudiencesList
              BASIC_PATH={BASIC_PATH}
              name={`${BASIC_PATH}.customAudiences`}
              control={control}
              disabled={isDisabledFields}
            />
            <Divider sx={{ my: 2 }} />
            <AdsGroupFormCustomAudiencesList
              BASIC_PATH={BASIC_PATH}
              name={`${BASIC_PATH}.excludedCustomAudiences`}
              control={control}
              disabled={isDisabledFields}
              isExcludedAudiences={true}
            />
          </Box>
        </CardContainer>

        {/* Demographics & Targeting */}
        <Grid container spacing={3}>
          {/* Age & Gender */}
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Person sx={{ color: "#FFFFFF", width: 24, height: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t(DEMOGRAPHICS_BASIC_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <Box sx={{ spaceY: 4 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t(AGE_RANGE_PLACEHOLDER)}
                  </Typography>
                  <Controller
                    name={`${BASIC_PATH}.ageRange`}
                    control={control}
                    render={({ field }) => (
                      <Slider
                        {...field}
                        sx={{
                          width: "calc(100% - 24px)",
                          ml: 1,
                          ".MuiSlider-track": {
                            height: 2,
                          },
                          ".MuiSlider-rail": {
                            backgroundColor: GREY_SLIDER_COLOR,
                            height: 4,
                          },
                          ".MuiSlider-mark": {
                            backgroundColor: "transparent",
                          },
                        }}
                        valueLabelDisplay="auto"
                        getAriaValueText={textNumberCompactFormat}
                        step={1}
                        marks={marksAge}
                        min={18}
                        max={65}
                        valueLabelFormat={textNumberCompactFormat}
                        disabled={isDisabledFields}
                      />
                    )}
                  />
                </Box>
                <Divider />
                <Box mt={4}>
                  <CheckBoxes
                    name={`${BASIC_PATH}.genders`}
                    disabled={isDisabledFields}
                    label={GENDERS_PLACEHOLDER}
                    options={[
                      {
                        key: "male",
                        label: MALES,
                      },
                      {
                        key: "female",
                        label: FEMALES,
                      },
                    ]}
                    control={control}
                    errors={errors}
                    rules={{
                      validate: () => {
                        if (!Object.values(genders).includes(true)) {
                          return t(FIELD_REQUIRED);
                        }
                      },
                    }}
                  />
                </Box>
              </Box>
            </CardContainer>
          </Grid>

          {/* Location */}
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnOutlined
                    sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t(LOCATIONS_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <SearchSegmentationsField
                name={`${BASIC_PATH}.locations`}
                type={ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE}
                mode="fetching"
                platform={platform}
                adAccountID={platformData?.adAccountID}
                integrationID={platformData?.integrationID}
                objective={objective}
                language={language}
                fullWidth
                variant="filled"
                disabled={isDisabledFields}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: !hasCustomAudiences,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
              <ClickableText
                onClick={() => onCopyValuesInOtherAds({ type: "locations" })}
              >
                {t(COPY_VALUE_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </CardContainer>
          </Grid>
        </Grid>

        {/* Advanced Targeting */}
        <Box>
          {/* Interests */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
              borderRadius: BORDER_RADIUS,
              p: 1,
            }}
          >
            <CardHeader
              onClick={() => setInterestsOpen(!interestsOpen)}
              sx={{
                cursor: "pointer",
              }}
              title={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Favorite
                      sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {t(INTERESTS_PLACEHOLDER)}
                    </Typography>
                  </Box>
                  {interestsOpen ? (
                    <KeyboardArrowUp sx={{ color: "#9CA3AF" }} />
                  ) : (
                    <KeyboardArrowDown sx={{ color: "#9CA3AF" }} />
                  )}
                </Box>
              }
            />
            {interestsOpen && (
              <CardContent>
                <SearchSegmentationsField
                  name={`${BASIC_PATH}.interests`}
                  type={ADS_CAMPAIGN_SEARCH_SEGMENTATION_INTERESTS_TYPE}
                  mode="fetching"
                  platform={platform}
                  adAccountID={platformData?.adAccountID}
                  integrationID={platformData?.integrationID}
                  objective={objective}
                  language={language}
                  fullWidth
                  variant="filled"
                  disabled={isDisabledFields}
                  helperText={
                    hasCustomAudiences
                      ? t(RETARGETING_EMPTY_SEGMENTATION_CLARIFICATION)
                      : ""
                  }
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: false,
                      message: t(FIELD_REQUIRED),
                    },
                  }}
                />
              </CardContent>
            )}
          </Card>

          {/* Behaviors */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
              borderRadius: BORDER_RADIUS,
              p: 1,
            }}
          >
            <CardHeader
              onClick={() => setBehaviorOpen(!behaviorOpen)}
              sx={{
                cursor: "pointer",
              }}
              title={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TrendingUp
                      sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {t(BEHAVIORS_PLACEHOLDER)}
                    </Typography>
                  </Box>
                  {behaviorOpen ? (
                    <KeyboardArrowUp sx={{ color: "#9CA3AF" }} />
                  ) : (
                    <KeyboardArrowDown sx={{ color: "#9CA3AF" }} />
                  )}
                </Box>
              }
            />
            {behaviorOpen && (
              <CardContent>
                <SearchSegmentationsField
                  name={`${BASIC_PATH}.behaviors`}
                  type={ADS_CAMPAIGN_SEARCH_SEGMENTATION_BEHAVIORS_TYPE}
                  mode="load"
                  groupBy={["path", 0]}
                  platform={platform}
                  adAccountID={platformData?.adAccountID}
                  integrationID={platformData?.integrationID}
                  objective={objective}
                  language={language}
                  fullWidth
                  variant="filled"
                  disabled={isDisabledFields}
                  helperText={
                    hasCustomAudiences
                      ? t(RETARGETING_EMPTY_SEGMENTATION_CLARIFICATION)
                      : ""
                  }
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: false,
                      message: t(FIELD_REQUIRED),
                    },
                  }}
                />
              </CardContent>
            )}
          </Card>

          {/* Demographics */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
              borderRadius: BORDER_RADIUS,
              p: 1,
            }}
          >
            <CardHeader
              onClick={() => setDemographyOpen(!demographyOpen)}
              sx={{
                cursor: "pointer",
              }}
              title={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <People
                      sx={{
                        color: "#FFFFFF",
                        width: 24,
                        height: 24,
                        fontWeight: "bold",
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {t(DEMOGRAPHICS_PLACEHOLDER)}
                    </Typography>
                  </Box>
                  {demographyOpen ? (
                    <KeyboardArrowUp sx={{ color: "#9CA3AF" }} />
                  ) : (
                    <KeyboardArrowDown sx={{ color: "#9CA3AF" }} />
                  )}
                </Box>
              }
            />
            {demographyOpen && (
              <CardContent>
                <SearchSegmentationsField
                  name={`${BASIC_PATH}.demographics`}
                  type={ADS_CAMPAIGN_SEARCH_SEGMENTATION_DEMOGRAPHICS_TYPE}
                  mode="load"
                  groupBy={["path", 0]}
                  platform={platform}
                  adAccountID={platformData?.adAccountID}
                  integrationID={platformData?.integrationID}
                  objective={objective}
                  language={language}
                  fullWidth
                  variant="filled"
                  disabled={isDisabledFields}
                  helperText={
                    hasCustomAudiences
                      ? t(RETARGETING_EMPTY_SEGMENTATION_CLARIFICATION)
                      : ""
                  }
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: false,
                      message: t(FIELD_REQUIRED),
                    },
                  }}
                />
              </CardContent>
            )}
          </Card>
        </Box>
      </Box>
      <Box
        sx={{
          width: isTabletOrMobile || showSlideChat ? "100%" : "400px",
          position: isTabletOrMobile || showSlideChat ? "static" : "sticky",
          top: isTabletOrMobile ? "auto" : 0,
          alignSelf: "flex-start",
          height: "fit-content",
          overflow: "auto",
          order: isTabletOrMobile || showSlideChat ? 1 : 2,
        }}
      >
        <ReservationEstimatesPreview BASIC_PATH={BASIC_PATH} />
      </Box>
    </Box>
  );
};

export default AdGroupBTReservedMetaForm;
